import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import { cn } from '../../utils/formatters';

const defaultColors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#22c55e', '#3b82f6'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-xl p-3 shadow-glass">
        <p className="text-sm font-medium text-white/90 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-white/60">{entry.name}:</span>
            <span className="text-white/90 font-medium">
              {typeof entry.value === 'number' 
                ? entry.value.toLocaleString('pt-BR')
                : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const GlassChart = ({
  type = 'line',
  data,
  dataKeys,
  xAxisKey = 'name',
  colors = defaultColors,
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  height = 300,
  className,
  gradient = true,
  stacked = false,
}) => {
  const chartProps = {
    data,
    margin: { top: 10, right: 10, left: 0, bottom: 0 },
  };

  const renderChart = () => {
    switch (type) {
      case 'line':
        return (
          <LineChart {...chartProps}>
            {showGrid && (
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            )}
            <XAxis
              dataKey={xAxisKey}
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            <YAxis
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
            {dataKeys.map((key, index) => (
              <Line
                key={key.key || key}
                type="monotone"
                dataKey={key.key || key}
                name={key.name || key}
                stroke={colors[index % colors.length]}
                strokeWidth={3}
                dot={{ fill: colors[index % colors.length], strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, stroke: colors[index % colors.length], strokeWidth: 2 }}
              />
            ))}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart {...chartProps}>
            {showGrid && (
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            )}
            <XAxis
              dataKey={xAxisKey}
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            <YAxis
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
            {dataKeys.map((key, index) => (
              <defs key={key.key || key}>
                <linearGradient id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors[index % colors.length]} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={colors[index % colors.length]} stopOpacity={0} />
                </linearGradient>
              </defs>
            ))}
            {dataKeys.map((key, index) => (
              <Area
                key={key.key || key}
                type="monotone"
                dataKey={key.key || key}
                name={key.name || key}
                stroke={colors[index % colors.length]}
                fill={`url(#gradient-${index})`}
                strokeWidth={2}
                stackId={stacked ? 'stack' : undefined}
              />
            ))}
          </AreaChart>
        );

      case 'bar':
        return (
          <BarChart {...chartProps}>
            {showGrid && (
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            )}
            <XAxis
              dataKey={xAxisKey}
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            <YAxis
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
            {dataKeys.map((key, index) => (
              <Bar
                key={key.key || key}
                dataKey={key.key || key}
                name={key.name || key}
                fill={colors[index % colors.length]}
                radius={[4, 4, 0, 0]}
                stackId={stacked ? 'stack' : undefined}
              />
            ))}
          </BarChart>
        );

      case 'pie':
        return (
          <PieChart {...chartProps}>
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={5}
              dataKey={dataKeys[0]?.key || dataKeys[0] || 'value'}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
          </PieChart>
        );

      case 'radar':
        return (
          <RadarChart {...chartProps} cx="50%" cy="50%" outerRadius="80%">
            <PolarGrid stroke="rgba(255,255,255,0.1)" />
            <PolarAngleAxis
              dataKey={xAxisKey}
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12 }}
            />
            <PolarRadiusAxis
              tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
              stroke="rgba(255,255,255,0.1)"
            />
            {dataKeys.map((key, index) => (
              <Radar
                key={key.key || key}
                name={key.name || key}
                dataKey={key.key || key}
                stroke={colors[index % colors.length]}
                fill={colors[index % colors.length]}
                fillOpacity={0.3}
              />
            ))}
            {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
          </RadarChart>
        );

      default:
        return null;
    }
  };

  return (
    <div className={cn('w-full', className)}>
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

export default GlassChart;
